import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Terminal, ExternalLink } from 'lucide-react';

import DFMReport from './DFMReport';
import CostBreakdown from './CostBreakdown';
import ApprovalGate from './ApprovalGate';
import FeatureList from './FeatureList';
import DrawingReconcile from './DrawingReconcile';
import AgentTrace from './AgentTrace';

export default function MarkdownRenderer({ content = '', onFocusFeatures }) {
  const [copiedCode, setCopiedCode] = useState(null);

  const handleCopy = (text, id) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  return (
    <div className="prose prose-sm max-w-none text-slate-800 leading-relaxed text-xs sm:text-sm">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code Block / OpenUI Interceptor
          code({ inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const lang = match ? match[1] : '';
            const rawContent = String(children).replace(/\n$/, '');

            // 1. OpenUI Generative UI Block Interceptor
            if (!inline && (lang === 'openui' || lang === 'openui_lang')) {
              try {
                const parsed = JSON.parse(rawContent);
                const componentName = parsed.component || parsed.name;
                const componentProps = parsed.props || parsed;

                switch (componentName) {
                  case 'DFMReport':
                    return <DFMReport {...componentProps} onFocusFeatures={onFocusFeatures} />;
                  case 'CostBreakdown':
                    return <CostBreakdown {...componentProps} />;
                  case 'ApprovalGate':
                    return <ApprovalGate {...componentProps} />;
                  case 'FeatureList':
                    return <FeatureList {...componentProps} />;
                  case 'DrawingReconcile':
                    return <DrawingReconcile {...componentProps} />;
                  case 'AgentTrace':
                    return <AgentTrace {...componentProps} />;
                  default:
                    return (
                      <div className="p-3 bg-slate-100 rounded-xl border border-slate-200 font-mono text-xs my-2">
                        <span className="font-bold text-blue-600">[{componentName}]</span>
                        <pre className="mt-1 overflow-x-auto text-[11px] text-slate-700">
                          {JSON.stringify(componentProps, null, 2)}
                        </pre>
                      </div>
                    );
                }
              } catch {
                // If not valid JSON, check if it's OpenUI Lang syntax
                return (
                  <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl my-2 text-xs">
                    <div className="font-semibold text-blue-900 mb-1">OpenUI Lang Directive</div>
                    <pre className="font-mono text-[11px] text-blue-800 whitespace-pre-wrap">
                      {rawContent}
                    </pre>
                  </div>
                );
              }
            }

            // 2. Inline Code
            if (inline) {
              return (
                <code
                  className="px-1.5 py-0.5 rounded bg-slate-100 text-blue-700 font-mono text-[11px] font-semibold border border-slate-200/60"
                  {...props}
                >
                  {children}
                </code>
              );
            }

            // 3. Standard Fenced Code Block (G-code, Python, JSON, Bash)
            const codeId = Math.random().toString(36).substring(7);
            const isCopied = copiedCode === codeId;

            return (
              <div className="relative my-3 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 text-slate-100 shadow-xs">
                {/* Header */}
                <div className="flex items-center justify-between px-3.5 py-1.5 bg-slate-800/80 border-b border-slate-700 text-xs select-none">
                  <div className="flex items-center gap-1.5 text-slate-400 font-mono text-[10px]">
                    <Terminal className="w-3 h-3 text-blue-400" />
                    <span>{lang ? lang.toUpperCase() : 'CODE'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(rawContent, codeId)}
                    className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    {isCopied ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-400" />
                        <span className="text-emerald-400">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Pre / Code */}
                <pre className="p-3 text-[11px] font-mono overflow-x-auto leading-relaxed text-slate-200">
                  <code>{children}</code>
                </pre>
              </div>
            );
          },

          // Tables
          table({ children }) {
            return (
              <div className="my-3 overflow-x-auto border border-slate-200 rounded-xl shadow-2xs">
                <table className="w-full text-left border-collapse text-xs">{children}</table>
              </div>
            );
          },
          thead({ children }) {
            return (
              <thead className="bg-slate-100/90 text-slate-700 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider">
                {children}
              </thead>
            );
          },
          th({ children }) {
            return <th className="py-2 px-3 font-semibold">{children}</th>;
          },
          td({ children }) {
            return <td className="py-2 px-3 border-b border-slate-100 text-slate-700">{children}</td>;
          },
          tr({ children }) {
            return <tr className="hover:bg-slate-50/80 transition-colors">{children}</tr>;
          },

          // Blockquotes
          blockquote({ children }) {
            return (
              <blockquote className="border-l-4 border-blue-500 pl-3.5 my-2.5 italic text-slate-600 bg-blue-50/30 py-1 rounded-r-lg text-xs">
                {children}
              </blockquote>
            );
          },

          // Headings
          h1({ children }) {
            return <h1 className="text-base sm:text-lg font-extrabold text-slate-900 mt-3 mb-1.5">{children}</h1>;
          },
          h2({ children }) {
            return <h2 className="text-sm sm:text-base font-bold text-slate-900 mt-2.5 mb-1">{children}</h2>;
          },
          h3({ children }) {
            return <h3 className="text-xs sm:text-sm font-bold text-slate-800 mt-2 mb-1">{children}</h3>;
          },

          // Links
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 underline font-medium inline-flex items-center gap-0.5"
              >
                <span>{children}</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
            );
          },

          // Lists
          ul({ children }) {
            return <ul className="list-disc list-inside space-y-1 my-1.5 text-slate-700">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="list-decimal list-inside space-y-1 my-1.5 text-slate-700">{children}</ol>;
          },
          li({ children }) {
            return <li className="leading-relaxed text-xs sm:text-sm">{children}</li>;
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
